import isUndefined from 'lodash-es/isUndefined';

export interface UndoManagerDelegate<T> {
  takeSnapshot: () => T;
  measureSnapshot: (snapshot: T) => number;
  restoreSnapshot: (snapshot: T) => void;
}

// Total string length, assuming that's what's used by measureSnapshot().
const DEFAULT_MAX_SIZE = 5 * 1024 * 1024;

export class UndoManager<T> {
  constructor(delegate: UndoManagerDelegate<T>, maxSize = DEFAULT_MAX_SIZE) {
    this.delegate = delegate;
    this.maxSize = maxSize;
  }

  readonly delegate: UndoManagerDelegate<T>;
  readonly maxSize: number;

  private lastSnapshot: T | undefined;
  private isLastSnapshotCommitted = false;
  private readonly undoStack: T[] = [];
  private undoStackSize = 0;
  private readonly redoStack: T[] = [];

  private pushUndoSnapshot(snapshot: T) {
    this.undoStack.push(snapshot);
    this.undoStackSize += this.delegate.measureSnapshot(snapshot);

    if (this.undoStackSize > this.maxSize) {
      let index = this.undoStack.length - 1;
      let size = this.delegate.measureSnapshot(this.undoStack[index]);

      while (index > 0 && size < this.maxSize) {
        const nextSize = this.delegate.measureSnapshot(this.undoStack[index - 1]);
        if (size + nextSize > this.maxSize) {
          break;
        }

        index -= 1;
        size += nextSize;
      }

      this.undoStack.splice(0, index);
      this.undoStackSize = size;
    }
  }

  private popUndoSnapshot(): T | undefined {
    const snapshot = this.undoStack.pop();

    if (!isUndefined(snapshot)) {
      this.undoStackSize -= this.delegate.measureSnapshot(snapshot);
    }

    return snapshot;
  }

  takeSnapshot() {
    const snapshot = this.delegate.takeSnapshot();
    if (snapshot === this.lastSnapshot) {
      return;
    }

    // Something changed, so push the last committed snapshot onto the undo stack.
    if (!isUndefined(this.lastSnapshot) && this.isLastSnapshotCommitted) {
      this.pushUndoSnapshot(this.lastSnapshot);
    }

    // Something changed, so clear the redo stack.
    this.redoStack.splice(0, this.redoStack.length);

    // Begin a new transaction.
    this.lastSnapshot = snapshot;
    this.isLastSnapshotCommitted = false;
  }

  commitLastSnapshot() {
    if (isUndefined(this.lastSnapshot)) {
      // eslint-disable-next-line no-console
      console.warn('Expected takeSnapshot() at least once before commitLastSnapshot()');
      return;
    }

    // Commit the active transaction.
    this.isLastSnapshotCommitted = true;
  }

  get canUndo() {
    return !isUndefined(this.lastSnapshot) && this.undoStack.length > 0;
  }

  get canRedo() {
    return !isUndefined(this.lastSnapshot) && this.redoStack.length > 0;
  }

  undo() {
    const undoSnapshot = this.popUndoSnapshot();
    if (isUndefined(undoSnapshot)) {
      return;
    }

    if (isUndefined(this.lastSnapshot)) {
      throw new Error('Must takeSnapshot() at least once before undo()');
    }

    // Save the current snapshot for redo, and restore the previous snapshot.
    const redoSnapshot = this.lastSnapshot;
    this.delegate.restoreSnapshot(undoSnapshot);

    // Now that we got here without exceptions, update our state.
    this.isLastSnapshotCommitted = true;
    this.lastSnapshot = undoSnapshot;
    this.redoStack.push(redoSnapshot);
  }

  redo() {
    const redoSnapshot = this.redoStack.pop();
    if (isUndefined(redoSnapshot)) {
      return;
    }

    if (isUndefined(this.lastSnapshot)) {
      throw new Error('Must takeSnapshot() at least once before redo()');
    }

    // Save the current snapshot for undo, and restore the next snapshot.
    const undoSnapshot = this.lastSnapshot;
    this.delegate.restoreSnapshot(redoSnapshot);

    // Now that we got here without exceptions, update our state.
    this.isLastSnapshotCommitted = true;
    this.lastSnapshot = redoSnapshot;
    this.pushUndoSnapshot(undoSnapshot);
  }
}
